import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Clock } from 'lucide-react';
import { C } from '../data/constants.js';
import type { DeadTimePhase } from '../data/constants.js';
import { EmptyState } from './EmptyState.js';

interface DeadTimeBreakdownProps {
  data?: DeadTimePhase[];
}

export function DeadTimeBreakdown({ data }: DeadTimeBreakdownProps): React.ReactElement {
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="Dead Time Data Not Available"
        message="Dead time breakdown by workflow phase is not yet being collected for this facility."
      />
    );
  }

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.cardBorder}`,
        borderRadius: 16,
        padding: '20px 24px',
        animation: 'insights-fadeSlideUp 0.5s ease 150ms both',
      }}
    >
      <h4
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: C.textMid,
          margin: '0 0 16px',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        Dead Time by Workflow Phase
      </h4>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} layout="vertical" barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 10, fill: C.textFaint }}
            axisLine={false}
            tickLine={false}
            unit=" min"
          />
          <YAxis
            type="category"
            dataKey="phase"
            tick={{ fontSize: 11, fill: C.textMid }}
            axisLine={false}
            tickLine={false}
            width={120}
          />
          <Tooltip
            contentStyle={{
              background: '#14161A',
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 10,
              fontSize: 12,
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
          <Bar dataKey="current" stackId="a" fill={C.purple} radius={[0, 0, 0, 0]} name="Current" />
          <Bar
            dataKey="saved"
            stackId="a"
            fill={`${C.accent}80`}
            radius={[0, 4, 4, 0]}
            name="Time Saved"
          />
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', gap: 20, marginTop: 8, fontSize: 11 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textDim }}>
          <div style={{ width: 12, height: 4, borderRadius: 2, background: C.purple }} /> Current
          Duration
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textDim }}>
          <div style={{ width: 12, height: 4, borderRadius: 2, background: `${C.accent}80` }} />{' '}
          Time Saved from Baseline
        </span>
      </div>
    </div>
  );
}
